import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useAuth } from "../auth/AuthContext";
import { getDocKey } from "./searchGroupUtils";
import { sendToServer as uiLog } from "../logs/LogsContext";
import { getApiBase } from "../../utils/env";

const SearchCtx = createContext(null);

export function SearchProvider({ children }) {
  const { token } = useAuth();
  const [query, setQuery] = useState("");
  const [dbs, setDbs] = useState([]); // [{dbName}]
  const [dbMeta, setDbMeta] = useState([]); // raw GetDatabases response for field metadata
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rawResults, setRawResults] = useState(null);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  // Which engine produced the last executed search expression: 'openai' | 'server' | 'local' | null
  const [interpreter, setInterpreter] = useState(null);
  const [lastSearchExpression, setLastSearchExpression] = useState(null);
  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("search_history_v1") || "[]");
    } catch {
      return [];
    }
  }); // [{ q, ts }]
  const [saved, setSaved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("saved_searches_v1") || "[]");
    } catch {
      return [];
    }
  }); // [{ name, q, ts }]

  // Currently selected document (from results list) for the details pane
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [recentDocs, setRecentDocs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("recent_docs_v1") || "[]");
    } catch {
      return [];
    }
  }); // [{id?, title?, excerpt?, ts, raw: obj}]
  // Checked (multi-selected) documents in results list: Set of doc keys
  const [checkedDocs, setCheckedDocs] = useState(new Set());
  // Pinned documents for cross-search comparison (persisted to localStorage)
  const [pinnedForCompare, setPinnedForCompare] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pinned_for_compare_v1") || "[]");
    } catch {
      return [];
    }
  });
  // Currently selected database group (from results list drilldown)
  const [selectedGroup, setSelectedGroup] = useState(null); // { name, items } | null
  // Optional flow runner loaded into the center results panel
  const [centerFlow, setCenterFlow] = useState(null); // flow name or null
  // Content-governance folder browse: the folder currently being browsed (null = root/none). Shared
  // so the sidebar folder list and the results-pane Explorer drive the same browse view.
  const [browseFolderId, setBrowseFolderId] = useState(null);
  // "Search only what I have filed" — the personal layer as a scope on the ordinary search rather than a
  // separate index: same expression, same result rows, restricted to this person's filings.
  //
  // Deliberately NOT persisted. The AI toggle beside it is remembered because forgetting it only changes
  // how a query is parsed; forgetting THIS one would silently hide documents on a later visit, and a
  // search that quietly answers a narrower question than the one asked is the worst kind of stale setting.
  const [workspaceScope, setWorkspaceScope] = useState(false);

  const CORE_API_BASE = useMemo(() => getApiBase(), []);

  // No static flags; read from env/window/localStorage dynamically within runSearch

  // Load registered databases after token is available
  useEffect(() => {
    let abort = false;
    async function loadDbs() {
      if (!token) return;
      try {
        const resp = await fetch(`${CORE_API_BASE}/api/Admin/GetDatabases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (abort) return;
        const arr = Array.isArray(data) ? data : [];
        setDbMeta(arr);
        const list = arr
          .map((x) => x?.name || x?.displayName || x?.dbName)
          .filter(Boolean)
          .map((name) => ({ dbName: name }));
        setDbs(list);
      } catch (e) {
        if (!abort) setError("Failed to load databases");
      }
    }
    loadDbs();
    return () => {
      abort = true;
    };
  }, [token, CORE_API_BASE]);

  const parseToSearchExpression = useCallback((text) => {
    if (!text) return "";
    let trimmed = text.trim();
    // 1) Power user JSON passes through
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) return trimmed;

    // Utilities for NLP mapping
    const toSingleQuotedJson = (obj) =>
      JSON.stringify(obj)
        .replace(/\u2028|\u2029/g, " ")
        .replace(/\\/g, "\\\\")
        .replace(/"/g, "'");

    const isNumeric = (v) => /^-?\d+(?:\.\d+)?$/.test(v);

    // Field normalization and value coercion
    const canonicalField = (raw) => {
      if (!raw) return raw;
      const r = raw
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[^a-z0-9]/g, "");
      const map = new Map([
        ["archiveno", "ArchiveNo"],
        ["archive", "ArchiveNo"],
        ["archivenumber", "ArchiveNo"],
        ["account", "ArchiveNo"],
        ["client", "ArchiveNo"],
        ["customer", "ArchiveNo"],
        ["documentdate", "DocumentDate"],
        ["date", "DocumentDate"],
        ["id", "Id"],
        ["docid", "Id"],
        ["documentid", "Id"],
        ["fileextension", "FileExtension"],
        ["extension", "FileExtension"],
        ["ext", "FileExtension"],
        ["type", "FileExtension"],
        ["file", "OriginalFileName"],
        ["filename", "OriginalFileName"],
        ["name", "OriginalFileName"],
        ["originalfilename", "OriginalFileName"],
      ]);
      return map.get(r) || raw;
    };

    const coerceForField = (field, rawVal) => {
      const f = canonicalField(field);
      const s = String(rawVal);
      if (f === "DocumentDate") {
        const d = parseDate(s);
        return d || s; // if parse fails, keep as string
      }
      if (f === "Id") {
        return isNumeric(s) ? Number(s) : s;
      }
      // Treat common ids as string (ArchiveNo, names, extensions)
      return s;
    };
    const parseDate = (s) => {
      // Try YYYY-MM-DD, YYYY/MM/DD or natural words like today, yesterday
      const t = s.toLowerCase();
      const now = new Date();
      const startOfDay = (d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (t === "today") return startOfDay(now).toISOString();
      if (t === "yesterday") {
        const d = new Date(now);
        d.setDate(d.getDate() - 1);
        return startOfDay(d).toISOString();
      }
      // Fallback Date.parse
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) return dt.toISOString();
      return null;
    };

    const getRelativeRange = (phrase) => {
      const now = new Date();
      const startOfDay = (d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
      const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
      const endOfDayIso = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999
      ).toISOString();

      const p = phrase.toLowerCase();
      if (p.includes("last year")) {
        const y = now.getFullYear() - 1;
        return [
          new Date(y, 0, 1).toISOString(),
          new Date(y, 11, 31, 23, 59, 59, 999).toISOString(),
        ];
      }
      if (p.includes("this year")) {
        return [startOfYear(now).toISOString(), endOfDayIso];
      }
      if (p.includes("last month")) {
        const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        const end = new Date(
          d.getFullYear(),
          d.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        return [start.toISOString(), end.toISOString()];
      }
      if (p.includes("this month")) {
        return [startOfMonth(now).toISOString(), endOfDayIso];
      }
      const m = p.match(
        /last\s+(\d+)\s*(day|days|week|weeks|month|months|year|years)/
      );
      if (m) {
        const n = parseInt(m[1], 10);
        const unit = m[2];
        const start = new Date(now);
        if (unit.startsWith("day")) start.setDate(start.getDate() - n);
        else if (unit.startsWith("week"))
          start.setDate(start.getDate() - n * 7);
        else if (unit.startsWith("month")) start.setMonth(start.getMonth() - n);
        else if (unit.startsWith("year"))
          start.setFullYear(start.getFullYear() - n);
        return [startOfDay(start).toISOString(), endOfDayIso];
      }

      // Month names: "last june", "this june", "june 2025", "in/from june"
      const MONTHS = {
        january: 0, jan: 0,
        february: 1, feb: 1,
        march: 2, mar: 2,
        april: 3, apr: 3,
        may: 4,
        june: 5, jun: 5,
        july: 6, jul: 6,
        august: 7, aug: 7,
        september: 8, sep: 8, sept: 8,
        october: 9, oct: 9,
        november: 10, nov: 10,
        december: 11, dec: 11,
      };
      const monthAlt = Object.keys(MONTHS).join("|");
      const monthRange = (year, month) => [
        new Date(year, month, 1).toISOString(),
        new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString(),
      ];

      // "<month> <year>" — explicit, takes priority over "last <month>".
      let mm = p.match(new RegExp(`\\b(${monthAlt})\\s+((?:19|20)\\d{2})\\b`, "i"));
      if (mm) return monthRange(parseInt(mm[2], 10), MONTHS[mm[1].toLowerCase()]);

      // "last <month>" → most recent past occurrence of that month.
      mm = p.match(new RegExp(`\\blast\\s+(${monthAlt})\\b`, "i"));
      if (mm) {
        const month = MONTHS[mm[1].toLowerCase()];
        const year = month >= now.getMonth() ? now.getFullYear() - 1 : now.getFullYear();
        return monthRange(year, month);
      }

      // "this <month>" → that month in the current year.
      mm = p.match(new RegExp(`\\bthis\\s+(${monthAlt})\\b`, "i"));
      if (mm) return monthRange(now.getFullYear(), MONTHS[mm[1].toLowerCase()]);

      // "in <month>" / "from <month>" / "during <month>" — most recent occurrence.
      mm = p.match(new RegExp(`\\b(?:in|from|during)\\s+(${monthAlt})\\b`, "i"));
      if (mm) {
        const month = MONTHS[mm[1].toLowerCase()];
        const year = month > now.getMonth() ? now.getFullYear() - 1 : now.getFullYear();
        return monthRange(year, month);
      }

      return null;
    };

    // Pre-process: keep "is"/"equals" as standalone operator tokens even when
    // the user forgot the space before the value (e.g. "is12120714").
    trimmed = trimmed.replace(/\b(is|equals)(\d)/gi, "$1 $2");

    // Pre-process: strip trailing "in <DBNAME>" when DBNAME matches one of the
    // currently-loaded databases, so the local parser doesn't treat "in RNB"
    // as part of a value. Database scoping itself is not yet wired through.
    const dbNames = (dbs || []).map((d) => d?.dbName).filter(Boolean);
    if (dbNames.length) {
      const dbAlt = dbNames
        .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("|");
      trimmed = trimmed
        .replace(new RegExp(`\\s+in\\s+(?:${dbAlt})\\s*$`, "i"), "")
        .trim();
    }

    const OP_MAP = {
      "=": "$eq",
      ":": "$eq",
      ">": "$gt",
      "<": "$lt",
      ">=": "$gte",
      "<=": "$lte",
      is: "$eq",
      equals: "$eq",
    };

    const parseSingleClause = (clause) => {
      const m = clause.match(
        /^([A-Za-z][\w\s]*?)\s*(>=|<=|>|<|=|:|is|equals)\s*(.+)$/i
      );
      if (!m) return null;
      const [, rawField, op, valueRaw] = m;
      const field = canonicalField(rawField.trim());
      const val = valueRaw.replace(/^['"]|['"]$/g, "").trim();
      const value = coerceForField(field, val);
      const o = OP_MAP[op.toLowerCase()] || "$eq";
      return { [field]: { [o]: value } };
    };

    // Multi-clause: "X is foo and Y is bar" → AND of per-clause filters.
    const splitClauses = trimmed.split(/\s+and\s+/i).map((s) => s.trim()).filter(Boolean);
    if (splitClauses.length > 1) {
      const parsed = splitClauses.map(parseSingleClause).filter(Boolean);
      if (parsed.length === splitClauses.length) {
        const combined = parsed.length === 1 ? parsed[0] : { $and: parsed };
        return toSingleQuotedJson(combined);
      }
    }

    // Single clause: field operator value
    const single = parseSingleClause(trimmed);
    if (single) return toSingleQuotedJson(single);

    // 3) Natural language parsing heuristics
    const q = trimmed;
    const clauses = [];

    // Date ranges
    const rel = getRelativeRange(q);
    if (rel) {
      clauses.push({ DocumentDate: { $gte: rel[0] } });
      clauses.push({ DocumentDate: { $lte: rel[1] } });
    }

    // explicit between: from X to Y / between X and Y
    const between = q.match(
      /(?:from|between)\s+([^\s].*?)\s+(?:to|and)\s+([^\s].*)/i
    );
    if (between) {
      const start = parseDate(between[1]);
      const end = parseDate(between[2]);
      if (start) clauses.push({ DocumentDate: { $gte: start } });
      if (end) clauses.push({ DocumentDate: { $lte: end } });
    }

    // Bare year or "from/in YYYY" → full year range
    const yearMatch = q.match(/\b(?:from|in|year)?\s*((?:19|20)\d{2})\b/i);
    if (yearMatch && !rel && !between) {
      const y = parseInt(yearMatch[1], 10);
      clauses.push({ DocumentDate: { $gte: new Date(y, 0, 1).toISOString() } });
      clauses.push({ DocumentDate: { $lte: new Date(y, 11, 31, 23, 59, 59, 999).toISOString() } });
    }

    // before/after/since
    const before = q.match(/before\s+([^\s].*)/i);
    if (before) {
      const d = parseDate(before[1]);
      if (d) clauses.push({ DocumentDate: { $lte: d } });
    }
    const after = q.match(/(?:after|since)\s+([^\s].*)/i);
    if (after) {
      const d = parseDate(after[1]);
      if (d) clauses.push({ DocumentDate: { $gte: d } });
    }

    // customer/archive/id keywords mapping to ArchiveNo or Id.
    // Match the most specific pattern first; when one fires, suppress the
    // looser ones so "customer with ID 123" doesn't also produce Id:123.
    let custMatched = false;

    // "customer with ID 123", "customer id 123", "client number 123",
    // "archive no 887766", "account #123"
    const custIdMatch = q.match(
      /\b(?:customer|account|client|archive|archiveno)\s+(?:with\s+)?(?:id|number|no\.?|#)\s*[:#]?\s*(\w+)/i
    );
    if (custIdMatch) {
      clauses.push({ ArchiveNo: { $eq: custIdMatch[1] } });
      custMatched = true;
    }

    // "ArchiveNo is/= 887766"
    if (!custMatched) {
      const archIs =
        q.match(/\barch(?:ive)?\s*no\s*(?:is|=|:)\s*(\w+)/i) ||
        q.match(/\barchiveno\s*(?:is|=|:)\s*(\w+)/i);
      if (archIs) {
        clauses.push({ ArchiveNo: { $eq: archIs[1] } });
        custMatched = true;
      }
    }

    // "customer 12345" / "archive 12345" — bare keyword + bare value.
    // Only fire if the value looks like an identifier (not a stop word).
    if (!custMatched) {
      const bare = q.match(/\b(?:customer|archive|account|client)\s+([\w-]+)\b/i);
      if (bare && !/^(with|id|number|no|the|a|an|of|for|from|on|in)$/i.test(bare[1])) {
        clauses.push({ ArchiveNo: { $eq: bare[1] } });
        custMatched = true;
      }
    }

    // Standalone "id 123" / "docid 123" — only when no customer match has
    // already claimed the value (the customer ID is the more useful filter).
    if (!custMatched) {
      const idm = q.match(/\b(id|docid|document id)\s*(?:=|:|#)?\s*(\d+)\b/i);
      if (idm) {
        clauses.push({ Id: { $eq: Number(idm[2]) } });
      }
    }

    // file type e.g. pdf, docx
    const ft = q.match(
      /\b(type|filetype|ext|extension)\s*(?:=|:)?\s*([a-z0-9]{2,5})\b/i
    );
    if (ft) {
      clauses.push({ FileExtension: { $eq: ft[2].toLowerCase() } });
    }

    // filename: or name:
    const fname = q.match(/\b(file(name)?|name)\s*(?:=|:)?\s*([^\s].*)/i);
    if (fname) {
      const term = fname[3].trim();
      clauses.push({ OriginalFileName: { $like: `%${term}%` } });
    }

    // Residual free-text terms: spread across common columns with $or of $like
    const stopwords = new Set([
      // articles, prepositions, conjunctions
      "in", "on", "for", "of", "and", "or", "the", "a", "an", "with",
      "from", "to", "by", "at", "as", "is", "are", "was", "were",
      // time helpers
      "last", "this", "year", "month", "week", "days", "day",
      "before", "after", "since", "between", "during", "today", "yesterday",
      // month names already consumed by getRelativeRange
      "january", "jan", "february", "feb", "march", "mar",
      "april", "apr", "may", "june", "jun", "july", "jul",
      "august", "aug", "september", "sep", "sept",
      "october", "oct", "november", "nov", "december", "dec",
      // domain nouns
      "documents", "document", "docs", "doc", "files", "file",
      "records", "record", "items", "item", "all",
      // request verbs / pronouns
      "show", "find", "get", "search", "bring", "fetch", "give",
      "list", "send", "obtain", "pull", "load", "display", "please",
      "my", "me", "us", "we",
      // identifier helpers already consumed by customer/id patterns
      "id", "ids", "number", "no", "customer", "client", "account",
      "archive", "archiveno",
    ]);
    const tokens = q
      .split(/[^\w.%@-]+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2 && !stopwords.has(t.toLowerCase()));
    const likeColumns = ["OriginalFileName", "ArchiveNo", "FileExtension"];
    const likeOrBlocks = [];
    for (const tkn of tokens) {
      // skip if already captured via id/customer/etc
      if (/^\d+$/.test(tkn) && clauses.some((c) => c.Id || c.ArchiveNo))
        continue;
      // skip if already captured as a year in a date clause
      if (/^(19|20)\d{2}$/.test(tkn) && clauses.some((c) => c.DocumentDate))
        continue;
      likeOrBlocks.push(
        likeColumns.map((col) => ({ [col]: { $like: `%${tkn}%` } }))
      );
    }
    // Merge tokens as AND of ORs
    for (const orGroup of likeOrBlocks) {
      clauses.push({ $or: orGroup });
    }

    if (clauses.length > 0) {
      // AND all clauses
      const filter = clauses.length === 1 ? clauses[0] : { $and: clauses };
      return toSingleQuotedJson(filter);
    }

    // 4) Fallback – treat as ArchiveNo exact or filename like
    // Try numeric => Id/ArchiveNo equality, else filename like
    if (isNumeric(trimmed)) {
      return toSingleQuotedJson({
        $or: [
          { Id: { $eq: Number(trimmed) } },
          { ArchiveNo: { $eq: trimmed } },
        ],
      });
    }
    return toSingleQuotedJson({
      $or: [
        { OriginalFileName: { $like: `%${trimmed}%` } },
        { ArchiveNo: { $like: `%${trimmed}%` } },
        { FileExtension: { $like: `%${trimmed}%` } },
      ],
    });
  }, [dbs]);

  // Try to extract an array of result rows from arbitrary API shapes.
  const tryExtractRows = useCallback((data) => {
    if (!data) return [];
    if (Array.isArray(data) && data.length && typeof data[0] === "object")
      return data;
    const candidates = [
      "results",
      "documents",
      "items",
      "rows",
      "records",
      "hits",
      "data",
    ];
    for (const key of candidates) {
      if (Array.isArray(data?.[key])) return data[key];
    }
    if (Array.isArray(data?.responses) && data.responses.length) {
      const resp0 = data.responses[0];
      for (const key of candidates) {
        if (Array.isArray(resp0?.[key])) return resp0[key];
      }
    }
    // Deep search first array of plain objects
    const stack = [data];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      if (Array.isArray(node)) {
        if (
          node.length &&
          typeof node[0] === "object" &&
          !Array.isArray(node[0])
        )
          return node;
      } else if (typeof node === "object") {
        for (const v of Object.values(node)) stack.push(v);
      }
    }
    return [];
  }, []);

  const deriveColumns = useCallback((arr) => {
    const set = new Set();
    for (const row of arr.slice(0, 25)) {
      Object.keys(row || {}).forEach((k) => set.add(k));
    }
    return Array.from(set);
  }, []);

  // `opts.taxonomyNodeIds` scopes the search to a set of taxonomy terms — the middle layer's
  // multi-valued axis, which is a JOIN on the server rather than a column in the filter expression, so
  // it cannot be expressed in the query text. Omitted, the request body is exactly what it was before.
  const runSearch = useCallback(
    async (text, opts) => {
      if (!token) {
        setError("You must be signed in to search.");
        return;
      }
      if (!dbs.length) {
        setError(
          "No databases available. Ensure the API is running and you are authorized."
        );
        return;
      }
      const userText = (text ?? query ?? "").trim();
      uiLog("Information", `Search: ${userText || "(empty)"}`, { query: userText });

      // A raw JSON filter (e.g. the folder-browse {"FolderId":{"$eq":id}}) is already a machine
      // filter — it must be used verbatim and NOT sent through the LLM interpreter (which would
      // mangle it into an empty/incorrect filter and return no rows).
      const isRawFilter = userText.startsWith("{") && userText.endsWith("}");

      // Database scope: when the query ends with "in <DBNAME>" and DBNAME
      // matches a known database, restrict the search to that one DB.
      let scopedDbs = dbs;
      const dbNames = (dbs || []).map((d) => d?.dbName).filter(Boolean);
      if (dbNames.length && userText) {
        const dbAlt = dbNames
          .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
          .join("|");
        const scopeMatch = userText.match(
          new RegExp(`\\s+in\\s+(${dbAlt})\\s*$`, "i")
        );
        if (scopeMatch) {
          const target = scopeMatch[1].toLowerCase();
          const filtered = (dbs || []).filter(
            (d) => (d?.dbName || "").toLowerCase() === target
          );
          if (filtered.length) scopedDbs = filtered;
        }
      }

      let searchExpression = "";
      let source = null; // 'openai' | 'server' | 'local'
      const parseBool = (v) => {
        if (v === true) return true;
        if (v === false) return false;
        const s = (v || "").toString().trim().toLowerCase();
        if (!s) return null;
        return s === "1" || s === "true" || s === "yes" || s === "on";
      };
      const toBool = (v) => {
        if (v === true) return true;
        if (v === false) return false;
        const s = (v || "").toString().trim().toLowerCase();
        return s === "1" || s === "true" || s === "yes" || s === "on";
      };
      // Read client-side OpenAI settings
      const clientApiKey =
        (typeof process !== "undefined" && process.env.REACT_APP_OPENAI_API_KEY) ||
        (typeof window !== "undefined" && window.__OPENAI_API_KEY__) ||
        (typeof localStorage !== "undefined" ? localStorage.getItem("openai_api_key") : null) ||
        null;
      const clientModel =
        (typeof process !== "undefined" && process.env.REACT_APP_OPENAI_MODEL) ||
        (typeof window !== "undefined" && window.__OPENAI_MODEL__) ||
        (typeof localStorage !== "undefined" ? localStorage.getItem("openai_model") : null) ||
        "gpt-4o-mini";
      const openAiFlag = parseBool(
        (typeof process !== "undefined" && process.env.REACT_APP_USE_OPENAI) ||
        (typeof window !== "undefined" && window.__USE_OPENAI__) ||
        (typeof localStorage !== "undefined" ? localStorage.getItem("use_openai") : null)
      );
      const useOpenAI = openAiFlag === null ? !!clientApiKey : openAiFlag;
      const serverFlagRaw =
        (typeof process !== "undefined" && process.env.REACT_APP_USE_SERVER_LLM) ||
        (typeof window !== "undefined" && window.__USE_SERVER_LLM__) ||
        (typeof localStorage !== "undefined" ? localStorage.getItem("use_server_llm") : null);
      const useServerLLM = toBool(serverFlagRaw);
      // Server LLM: call LLMSearchAndExecute which generates the filter AND runs the query
      // in one server-side round-trip, avoiding any client-side filter serialization issues.
      if (!searchExpression && useServerLLM && !isRawFilter) {
        try {
          setLoading(true);
          setError("");
          // The scope has to survive the natural-language path too, or turning it on and then phrasing the
          // query in words would quietly search everything.
          const llmBody = {
            queryText: userText,
            databases: scopedDbs,
            ...(workspaceScope ? { workspaceOnly: true } : {}),
          };
          const llmResp = await fetch(`${CORE_API_BASE}/api/Search/LLMSearchAndExecute`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(llmBody),
          });
          if (llmResp.ok) {
            const llmData = await llmResp.json();
            const r = tryExtractRows(llmData);
            setRawResults(llmData);
            setRows(r);
            setColumns(deriveColumns(r));
            setInterpreter("server");
            setLastSearchExpression(llmData?.searchExpression || llmData?.SearchExpression || null);
            setSelectedDoc(null);
            setLoading(false);
            const q2 = userText;
            if (q2) {
              setHistory((prev) => {
                const next = [...prev].filter((h) => h.q !== q2);
                next.unshift({ q: q2, ts: Date.now() });
                const limited = next.slice(0, 30);
                try { localStorage.setItem("search_history_v1", JSON.stringify(limited)); } catch {}
                return limited;
              });
            }
            return;
          } else {
            const errText = await llmResp.text();
            if (llmResp.status === 401 || llmResp.status === 403) {
              setError("AI key error: invalid or expired API key. Update it in Preferences → AI Settings.");
              setLoading(false);
              return;
            } else if (llmResp.status === 429) {
              setError("AI rate limit reached. Try again in a moment.");
              setLoading(false);
              return;
            } else if (llmResp.status === 503) {
              setError("Server LLM not configured. Add an API key in Admin → AI Settings.");
              setLoading(false);
              return;
            }
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.warn("[SEARCH DEBUG] LLMSearchAndExecute failed:", llmResp.status, errText);
            }
            // Fall through to local parser
            setLoading(false);
          }
        } catch (_) {
          setLoading(false);
          // ignore, fallback to local parser below
        }
      }
      // Client-side OpenAI: call LLMSearch (returns filter only) then SearchContent
      if (!searchExpression && useOpenAI && !isRawFilter) {
        try {
          const llmBody = {
            queryText: userText,
            databases: scopedDbs,
            ...(clientApiKey ? { clientApiKey, clientModel } : {}),
          };
          const llmResp = await fetch(`${CORE_API_BASE}/api/Search/LLMSearch`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(llmBody),
          });
          if (llmResp.ok) {
            const llmData = await llmResp.json();
            const expr = llmData?.searchExpression || llmData?.SearchExpression;
            if (expr) {
              searchExpression = expr;
              source = "openai";
            }
          } else {
            const errText = await llmResp.text();
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.warn("[SEARCH DEBUG] LLMSearch failed:", llmResp.status, errText);
            }
            if (llmResp.status === 401 || llmResp.status === 403) {
              setError("AI key error: invalid or expired API key. Update it in Preferences → AI Settings.");
            } else if (llmResp.status === 429) {
              setError("AI rate limit reached. Try again in a moment.");
            }
          }
        } catch (_) {
          // ignore, fallback to local parser below
        }
      }
      // Fallback to client parser
      if (!searchExpression) {
        searchExpression = parseToSearchExpression(userText);
        source = "local";
      }
      if (!searchExpression) return;
      if (process.env.NODE_ENV !== "production") {
        // Dev aid: show how the query was translated
        // eslint-disable-next-line no-console
        console.info("[SEARCH DEBUG] searchExpression:", searchExpression);
      }
      setLoading(true);
      setError("");
      try {
        const taxonomyNodeIds = Array.isArray(opts?.taxonomyNodeIds) ? opts.taxonomyNodeIds : null;
        const body = {
          requests: [
            {
              requestID: `REQ_${Date.now()}`,
              databases: scopedDbs,
              searchExpression,
              ...(taxonomyNodeIds && taxonomyNodeIds.length ? { taxonomyNodeIds } : {}),
              // Only the flag travels — never an owner. The server resolves whose workspace this is from
              // the token, so there is no request shape that reads somebody else's filing.
              ...(workspaceScope ? { workspaceOnly: true } : {}),
            },
          ],
        };
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.info("[SEARCH DEBUG] request body:", body);
        }
        const resp = await fetch(`${CORE_API_BASE}/api/Search/SearchContent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        const raw = await resp.text();
        if (!resp.ok) throw new Error(raw || `HTTP ${resp.status}`);
        let data;
        try {
          data = raw ? JSON.parse(raw) : null;
        } catch {
          data = raw;
        }
        setRawResults(data);
        const r = tryExtractRows(data);
        setRows(r);
        setColumns(deriveColumns(r));
        setInterpreter(source);
        setLastSearchExpression(source !== "local" ? searchExpression : null);
        uiLog("Information", `Search returned ${r.length} result(s) for: ${userText}`, { count: r.length, interpreter: source });
        // Clear previous selection when new results arrive
        setSelectedDoc(null);

        // Update history (dedupe adjacent, keep max 30)
        const q = userText;
        if (q) {
          setHistory((prev) => {
            const next = [...prev];
            // remove any existing same q to move to front
            const filtered = next.filter((h) => h.q !== q);
            filtered.unshift({ q, ts: Date.now() });
            const limited = filtered.slice(0, 30);
            try {
              localStorage.setItem(
                "search_history_v1",
                JSON.stringify(limited)
              );
            } catch {}
            return limited;
          });
        }
      } catch (e) {
        uiLog("Warning", `Search failed: ${e.message || "unknown error"}`, { query: userText });
        setError(e.message || "Search failed");
        setRawResults(null);
        setRows([]);
        setColumns([]);
      } finally {
        setLoading(false);
      }
    },
    [
      token,
      dbs,
      dbMeta,
      parseToSearchExpression,
      CORE_API_BASE,
      query,
      tryExtractRows,
      deriveColumns,
      // Without this the toggle would only take effect on the search AFTER the one you asked for:
      // runSearch would keep the value captured when it was last rebuilt.
      workspaceScope,
    ]
  );

  // Provide a live preview of how the query is interpreted
  const interpreted = useMemo(() => {
    try {
      if (!query?.trim()) return "";
      return parseToSearchExpression(query);
    } catch {
      return "";
    }
  }, [query, parseToSearchExpression]);

  const value = {
    query,
    setQuery,
    loading,
    error,
    dbs,
    rawResults,
    rows,
    columns,
    hasResults: rows && rows.length > 0,
    runSearch,
    browseFolderId,
    setBrowseFolderId,
    workspaceScope,
    setWorkspaceScope,
    interpreted,
    interpreter,
    lastSearchExpression,
    history,
    popular: useMemo(() => {
      const counts = new Map();
      for (const h of history) counts.set(h.q, (counts.get(h.q) || 0) + 1);
      return Array.from(counts, ([q, c]) => ({ q, c }))
        .sort((a, b) => b.c - a.c || a.q.localeCompare(b.q))
        .slice(0, 10);
    }, [history]),
    clearHistory: () => {
      setHistory([]);
      try {
        localStorage.removeItem("search_history_v1");
      } catch {}
    },
    // Saved searches API
    saved,
    saveSearch: (q, name) => {
      const queryText = (q ?? query ?? "").trim();
      if (!queryText) return;
      const display = name || queryText;
      setSaved((prev) => {
        const filtered = prev.filter(
          (s) => s.q !== queryText && s.name !== display
        );
        const next = [
          { name: display, q: queryText, ts: Date.now() },
          ...filtered,
        ].slice(0, 50);
        try {
          localStorage.setItem("saved_searches_v1", JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    removeSaved: (nameOrQ) => {
      setSaved((prev) => {
        const next = prev.filter((s) => s.name !== nameOrQ && s.q !== nameOrQ);
        try {
          localStorage.setItem("saved_searches_v1", JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    toggleSavedFor: (q) => {
      const queryText = (q ?? query ?? "").trim();
      if (!queryText) return;
      const exists = saved.some((s) => s.q === queryText);
      if (exists) {
        const next = saved.filter((s) => s.q !== queryText);
        setSaved(next);
        try {
          localStorage.setItem("saved_searches_v1", JSON.stringify(next));
        } catch {}
      } else {
        const next = [
          { name: queryText, q: queryText, ts: Date.now() },
          ...saved,
        ].slice(0, 50);
        setSaved(next);
        try {
          localStorage.setItem("saved_searches_v1", JSON.stringify(next));
        } catch {}
      }
    },
    isSaved: useMemo(() => {
      const q = (query || "").trim();
      return !!(q && saved.some((s) => s.q === q));
    }, [saved, query]),
    // Selection state and helpers
    selectedDoc,
    setSelectedDoc,
    selectDocument: (doc) => {
      setSelectedDoc(doc || null);
      try {
        if (doc) {
          setRecentDocs((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const idKey = doc.Id ?? doc.id ?? doc.DocumentId ?? null;
            const matchIndex = next.findIndex((r) => {
              if (idKey != null && String(r.id ?? r.Id) === String(idKey))
                return true;
              const an =
                (doc.ArchiveNo ||
                  doc.archiveNo ||
                  doc.archiveno ||
                  doc.Archive) &&
                String(
                  doc.ArchiveNo || doc.archiveNo || doc.archiveno || doc.Archive
                );
              if (
                an &&
                String(r.archiveNo || r.ArchiveNo || r.archiveno) === an
              )
                return true;
              const fn =
                doc.OriginalFileName ||
                doc.originalFileName ||
                doc.name ||
                doc.fileName;
              if (fn && String(r.title) === String(fn)) return true;
              return false;
            });
            const entry = {
              id: idKey ?? undefined,
              title:
                doc.OriginalFileName ||
                doc.originalFileName ||
                doc.name ||
                doc.fileName ||
                doc.Id ||
                doc.ArchiveNo ||
                "Untitled",
              archiveNo:
                doc.ArchiveNo || doc.archiveNo || doc.archiveno || undefined,
              date:
                doc.DocumentDate ||
                doc.documentDate ||
                doc.createdAt ||
                undefined,
              ts: Date.now(),
              raw: doc,
            };
            if (matchIndex >= 0) next.splice(matchIndex, 1);
            next.unshift(entry);
            const limited = next.slice(0, 20);
            try {
              localStorage.setItem("recent_docs_v1", JSON.stringify(limited));
            } catch {}
            return limited;
          });
        }
      } catch {}
    },
    recentDocs,
    clearRecentDocs: () => {
      setRecentDocs([]);
      try {
        localStorage.removeItem("recent_docs_v1");
      } catch {}
    },
    // Checked docs state (multi-select in results)
    checkedDocs,
    setCheckedDocs,
    // Pinned for compare (cross-search)
    pinnedForCompare,
    pinForCompare: (doc) => {
      setPinnedForCompare((prev) => {
        const key = getDocKey(doc);
        if (prev.some((d) => getDocKey(d) === key)) return prev;
        const next = [...prev, doc].slice(0, 10);
        try { localStorage.setItem("pinned_for_compare_v1", JSON.stringify(next)); } catch {}
        return next;
      });
    },
    unpinForCompare: (doc) => {
      setPinnedForCompare((prev) => {
        const key = getDocKey(doc);
        const next = prev.filter((d) => getDocKey(d) !== key);
        try { localStorage.setItem("pinned_for_compare_v1", JSON.stringify(next)); } catch {}
        return next;
      });
    },
    clearCompare: () => {
      setPinnedForCompare([]);
      try { localStorage.removeItem("pinned_for_compare_v1"); } catch {}
    },
    isPinnedForCompare: (doc) => {
      if (!doc) return false;
      const key = getDocKey(doc);
      return pinnedForCompare.some((d) => getDocKey(d) === key);
    },
    // Selected group state for results footer
    selectedGroup,
    setSelectedGroup,
    // Center flow runner control
    centerFlow,
    openFlowInCenter: (flowName) => setCenterFlow(flowName || null),
    closeFlowInCenter: () => setCenterFlow(null),
    // Flow search result injection — lets FlowRunnerCore push results into the main panel
    setFlowResults: (flowRows) => {
      const r = Array.isArray(flowRows) ? flowRows : [];
      setRows(r);
      setRawResults(null);
      setColumns(deriveColumns(r));
      setSelectedDoc(null);
      setLoading(false);
      setError("");
    },
    clearFlowResults: () => {
      setRows([]);
      setRawResults(null);
      setColumns([]);
      setSelectedDoc(null);
    },
    clearResults: () => {
      setRows([]);
      setRawResults(null);
      setColumns([]);
      setSelectedDoc(null);
    },
  };

  return <SearchCtx.Provider value={value}>{children}</SearchCtx.Provider>;
}

export function useSearch() {
  const ctx = useContext(SearchCtx);
  if (!ctx) throw new Error("useSearch must be used within SearchProvider");
  return ctx;
}
